import {useMutation, useQuery} from '@tanstack/react-query'
import {useRouteContext} from '@tanstack/react-router'
import {addProductToBasket, applyCoupon, getBasket, updateQuantity} from '~/client/fn/basket'
import useLocaleParams from '~/hooks/useLocaleParams'
import {ProductVariant} from '~/gql/graphql'

export const useBasketQuery = () => {
    const params = useLocaleParams()

    return useQuery({
        queryKey: ['basket', params.locale],
        queryFn: async () => (await getBasket({data: {locale: params.locale}})) ?? null,
        refetchOnWindowFocus: 'always',
    })
}

type UseAddToBasketMutationProps = {
    onSuccess?: () => void
    onMutate?: () => void
}

export const useAddToBasketMutation = ({onSuccess, onMutate}: UseAddToBasketMutationProps) => {
    const {queryClient} = useRouteContext({from: '__root__'})

    return useMutation({
        mutationFn: async (variant: ProductVariant) => {
            await addProductToBasket({
                data: {
                    sku: variant.sku!,
                    sizeId: variant.size?.id!,
                    appearanceId: variant.appearance?.id!,
                },
            })
        },
        onSettled: () => queryClient.invalidateQueries({queryKey: ['basket']}),
        onSuccess: onSuccess,
        onMutate: onMutate,
    })
}

export const useBasketItemQuantityMutation = () => {
    const {queryClient} = useRouteContext({from: '__root__'})

    return useMutation({
        mutationFn: async (props: {
            productId: string
            sizeId: string
            appearanceId: string
            quantity: number
        }) => {
            await updateQuantity({data: {...props}})
        },
        onSettled: () => queryClient.invalidateQueries({queryKey: ['basket']}),
    })
}

type UsePromotionMutationProps = {
    onSuccess?: () => void
}

export const usePromotionMutation = (props: UsePromotionMutationProps) => {
    const {queryClient} = useRouteContext({from: '__root__'})

    return useMutation({
        mutationFn: async (promotionCode?: string) => {
            await applyCoupon({data: {code: promotionCode}})
        },
        onSettled: () => queryClient.invalidateQueries({queryKey: ['basket']}),
        ...props,
    })
}
